import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import $api from "@/zapi-admin/shared/api/axios";
import { AiLog, AiLogsByRequestIdSchema, AiLogsByRequestId } from "../model/types";
import { AppLoader } from "@/zapi-admin/shared/components/AppLoader";
import { JsonViewer } from "../../../shared/components/JsonViewer/ui/JsonViewer";
import { downloadAsJsonFile } from "@/zapi-admin/shared/lib/dowload";
import { MonacoTabs } from "@/zapi-admin/shared/components/MonacoTabs/ui/MonacoTabs";
import { getRelevantFieldsPrettyfied } from "../lib/jsonFields";
import { useSearchParams } from "react-router-dom";
import { PromptGraphViewer } from "./PromptGraphViewer/ui/PromptGraphViewer";
import { NodeDataViewer } from "./PromptGraphViewer/ui/NodeDataViewer";
import { getDataMapFromPromptGraph } from "../lib/promptGraphData";
import { TPromptGraphData, TPromptGraphJoinNode } from "@/zapi-admin/pages/AiLogsPage/model/promptGraphTypes";
import { ProjectSelect } from "@/zapi-admin/shared/components/ProjectSelect/ui/ProjectSelect";
import { UserSelect } from "@/zapi-admin/pages/AiLogsPage/ui/UserSelect";
import { useDebouncedValue } from "@/zapi-admin/shared/hooks/useDebouncedValue";
import { AILogsTable } from "@/zapi-admin/pages/AiLogsPage/ui/AiLogsTable";
import { AppButton, AppInput } from "@libs/zapi-ui/custom";
import { useAiLogDetails } from "@/zapi-admin/pages/AiLogsPage/lib/useAiLogDetails";
import { ChapterSelect } from "@/zapi-admin/shared/components/ChapterSelect/ui/ChapterSelect";
import { RefreshCw, Trash2 } from "lucide-react";
import { GenerationTypeSelect } from "@/zapi-admin/pages/AiLogsPage/ui/GenerationTypeSelect";

export function AiLogsView() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [project, setProject] = useState(() => localStorage.getItem("project") || "");
  const [user, setUser] = useState(() => localStorage.getItem("user") || "");
  const debouncedUser = useDebouncedValue(user, 300);

  const initialPage = parseInt(searchParams.get("page") || "1", 10);
  const [page, setPage] = useState(initialPage);
  const isUpdatingFromState = useRef(false);

  const initialRequestId = searchParams.get("requestId") || "";
  const [requestId, setRequestId] = useState(initialRequestId);
  const debouncedRequestId = useDebouncedValue(requestId, 300);
  const prevDebouncedRequestId = useRef(debouncedRequestId);

  const [chapterId, setChapterId] = useState("");
  const debouncedChapterId = useDebouncedValue(chapterId, 300);

  const [generationType, setGenerationType] = useState("");

  const [selectedLogId, setSelectedLogId] = useState<number | null>(null);
  const [jsonViewerDataField, setJsonViewerDataField] = useState<keyof AiLog | null>("requestParams");

  const [node, setNode] = useState<TPromptGraphJoinNode>(null);

  const [view, setView] = useState<"prettyfied" | "raw" | "prompt_graph">("prettyfied");
  const [isRefetching, setIsRefetching] = useState(false);
  const [isClearingCache, setIsClearingCache] = useState(false);
  const pageSize = 10;

  // Update URL when page state changes, preserving other params
  const handlePageChange = useCallback(
    (newPage: number) => {
      isUpdatingFromState.current = true;
      setPage(newPage);
      const params = new URLSearchParams(searchParams);
      params.set("page", newPage.toString());
      setSearchParams(params, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Reset page to 1 when search filters change
  useEffect(() => {
    if (prevDebouncedRequestId.current !== debouncedRequestId) {
      prevDebouncedRequestId.current = debouncedRequestId;
      handlePageChange(1);
    }
  }, [debouncedRequestId, handlePageChange]);

  // Sync page state from URL when searchParams change (but not when we're updating from state)
  useEffect(() => {
    if (isUpdatingFromState.current) {
      isUpdatingFromState.current = false;
      return;
    }
    const urlPage = parseInt(searchParams.get("page") || "1", 10);
    if (urlPage !== page) {
      setPage(urlPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["get-ai-logs", debouncedRequestId, page, project, debouncedChapterId, debouncedUser, generationType],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (debouncedRequestId) params.append("requestId", debouncedRequestId);
      if (debouncedChapterId) params.append("chapterId", debouncedChapterId);
      if (debouncedUser) params.append("userId", debouncedUser);
      if (project) params.append("projectId", project);
      if (generationType) params.append("generationType", generationType);
      params.append("page", page + "");
      params.append("pageSize", pageSize + "");
      const response = await $api.get<{ data: AiLogsByRequestId; total: number }>(
        "/admin/ai-logs/by-request?" + params.toString()
      );
      if (response?.data?.data) {
        const logs = AiLogsByRequestIdSchema.parse(response.data.data);
        return { logs, total: response.data.total };
      }
      return { logs: [], total: 0 };
    }
  });

  const { data: logDetails, isLoading: detailsLoading } = useAiLogDetails(selectedLogId);

  const handleViewDetails = (id: number) => {
    setSelectedLogId(id);
  };

  const handleRefetch = async () => {
    setIsRefetching(true);
    try {
      await refetch();
    } finally {
      setIsRefetching(false);
    }
  };

  const handleClearCache = async () => {
    if (!window.confirm("Are you sure you want to clear the AI cache? This will remove all cached AI responses.")) {
      return;
    }
    setIsClearingCache(true);
    try {
      const response = await $api.delete<{ message: string; deletedCount: number }>("/admin/ai-logs/cache");
      alert(`${response.data.message} (${response.data.deletedCount} keys deleted)`);
    } catch (error) {
      console.error("Failed to clear AI cache:", error);
      alert("Failed to clear AI cache");
    } finally {
      setIsClearingCache(false);
    }
  };

  const promptGraphData = useMemo(
    () => logDetails?.requestParams?.requestAnnotations?.promptGraphData as TPromptGraphData<string>,
    [logDetails]
  );

  const { dataMap, execMap } = getDataMapFromPromptGraph(promptGraphData);

  const prettyTabsData = useMemo(() => {
    if (!logDetails || !jsonViewerDataField) return null;
    const relevantFields = getRelevantFieldsPrettyfied(
      logDetails[jsonViewerDataField],
      jsonViewerDataField === "requestParams" ? "request" : "response",
      logDetails.type
    );
    if (jsonViewerDataField === "requestParams" && promptGraphData?.nodes[promptGraphData.rootNode]?.prompt) {
      const promptVis = {
        id: "Prompt",
        label: "Prompt (Prompt template)",
        value: promptGraphData?.nodes[promptGraphData.rootNode].prompt,
        language: "innSnip"
      };
      return [...relevantFields, promptVis];
    }
    return relevantFields;
  }, [logDetails, jsonViewerDataField, promptGraphData]);

  return (
    <div data-testid="ai-logs-view">
      <div className="flex gap-4  mb-6" data-testid="ai-logs-filters">
        <UserSelect userId={user} setUserId={setUser} />
        <ProjectSelect
          projectId={project}
          setProjectId={(v) => {
            setProject(v);
            handlePageChange(1);
          }}
        />
        <ChapterSelect chapterId={chapterId} setChapterId={setChapterId} projectId={project} />
        <GenerationTypeSelect
          generationType={generationType}
          setGenerationType={(v) => {
            setGenerationType(v);
            handlePageChange(1);
          }}
        />
        <div className="space-y-2 w-[258px]">
          <AppInput
            placeholder="Task Id"
            value={requestId}
            onChange={(e) => setRequestId(e.target.value)}
            className="rounded-[8px] bg-innoveraZinc-50"
            data-testid="ai-logs-request-id-input"
          />
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <AppButton
            appearance="outlined"
            color="zinc950"
            onClick={handleClearCache}
            disabled={isClearingCache}
            className="flex items-center gap-2 px-4 py-2 h-fit"
          >
            <Trash2 size={16} className={isClearingCache ? "animate-pulse" : ""} />
            {isClearingCache ? "Clearing..." : "Clear AI Cache"}
          </AppButton>
          <AppButton
            appearance="outlined"
            color="zinc950"
            onClick={handleRefetch}
            disabled={isLoading || isRefetching}
            className="flex items-center gap-2 px-4 py-2 h-fit"
          >
            <RefreshCw size={16} className={isRefetching ? "animate-spin" : ""} />
            {isRefetching ? "Refreshing..." : "Refresh"}
          </AppButton>
        </div>
      </div>

      <div className="overflow-x-auto">
        {isLoading && (
          <div className="flex items-center justify-center">
            <AppLoader />
          </div>
        )}

        {!isLoading && (
          <AILogsTable
            logs={data?.logs || []}
            pageSize={pageSize}
            page={page}
            total={data?.total}
            setPage={handlePageChange}
            onDetailClick={handleViewDetails}
          />
        )}
      </div>

      {selectedLogId && (
        <div
          onClick={() => setSelectedLogId(null)}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end"
          data-testid="ai-logs-detail-overlay"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-l-lg w-full max-w-6xl p-4 max-h-screen h-screen overflow-y-auto transition-transform duration-300 transform translate-x-0"
            data-testid="ai-logs-detail-panel"
          >
            <div className="flex items-center justify-between mb-4 w-full">
              <div className="flex items-center gap-4">
                <div className="flex gap-2">
                  <AppButton
                    appearance={jsonViewerDataField === "requestParams" ? "contained" : "outlined"}
                    color="zinc950"
                    className="px-4 py-2"
                    onClick={() => setJsonViewerDataField("requestParams")}
                  >
                    Request
                  </AppButton>
                  {logDetails?.errors && (
                    <AppButton
                      appearance={jsonViewerDataField === "errors" ? "contained" : "outlined"}
                      color="zinc950"
                      className="px-4 py-2"
                      onClick={() => setJsonViewerDataField("errors")}
                    >
                      Errors
                    </AppButton>
                  )}
                  {!logDetails?.errors && (
                    <AppButton
                      appearance={jsonViewerDataField === "response" ? "contained" : "outlined"}
                      color="zinc950"
                      className="px-4 py-2"
                      onClick={() => setJsonViewerDataField("response")}
                    >
                      Response
                    </AppButton>
                  )}
                </div>
                <div className="h-6 w-px bg-gray-300 mx-2" />
                <div className="flex gap-2">
                  <AppButton
                    appearance={view === "prettyfied" ? "contained" : "outlined"}
                    color="zinc950"
                    className="px-4 py-2"
                    onClick={() => setView("prettyfied")}
                  >
                    Prettyfied
                  </AppButton>
                  <AppButton
                    appearance={view === "raw" ? "contained" : "outlined"}
                    color="zinc950"
                    className="px-4 py-2"
                    onClick={() => setView("raw")}
                  >
                    Raw
                  </AppButton>
                  {jsonViewerDataField === "requestParams" && (
                    <AppButton
                      appearance={view === "prompt_graph" ? "contained" : "outlined"}
                      color="zinc950"
                      className="px-4 py-2"
                      onClick={() => setView("prompt_graph")}
                    >
                      Prompt Graph
                    </AppButton>
                  )}
                </div>
              </div>
              <div className="flex-1 flex justify-center">
                <h3 className="text-lg font-semibold">AI Log Details</h3>
              </div>
              <div className="flex gap-2">
                <AppButton
                  appearance="text"
                  color="zinc950"
                  size="sm"
                  onClick={() => {
                    if (!jsonViewerDataField || !logDetails) return;
                    downloadAsJsonFile(
                      logDetails[jsonViewerDataField],
                      `ai-log-${selectedLogId}-${jsonViewerDataField}`
                    );
                  }}
                >
                  Download as JSON
                </AppButton>
                <AppButton appearance="text" color="zinc950" size="sm" onClick={() => setSelectedLogId(null)}>
                  Close
                </AppButton>
              </div>
            </div>
            {detailsLoading && <div>Loading...</div>}
            {!detailsLoading && logDetails && view === "raw" && (
              <div className="h-[calc(100vh-100px)]">
                <JsonViewer json={jsonViewerDataField && logDetails[jsonViewerDataField]} />
              </div>
            )}
            {!detailsLoading && logDetails && view === "prettyfied" && (
              <div className="h-[calc(100vh-100px)]">
                <MonacoTabs files={prettyTabsData || []} execMap={execMap} dataMap={dataMap} />
              </div>
            )}
            {!detailsLoading && logDetails && view === "prompt_graph" && (
              <div className="h-[calc(100vh-100px)]">
                <PromptGraphViewer
                  data={jsonViewerDataField ? logDetails[jsonViewerDataField]?.requestAnnotations?.promptGraphData : {}}
                  onNodeClick={setNode}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <NodeDataViewer
        node={node}
        setNode={setNode}
        promptGraphData={
          jsonViewerDataField ? logDetails?.requestParams?.requestAnnotations?.promptGraphData : undefined
        }
      />
    </div>
  );
}
